import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Tooltip from '../../components/Tooltip';
import StatCard from '../../components/StatCard';
import StatusBadge from '../../components/StatusBadge';
import ExportButton from '../../components/ExportButton';
import Pagination from '../../components/Pagination';
import DateRangePicker from '../../components/DateRangePicker';
import CustomFulfillmentArtifactModal from './customFulfillmentArtifactModal';
import { getCustomFulfillmentReport } from '../../utils/queries';

const PAGE_SIZE = 20;

export default function CustomFulfillmentReport() {
	const { activeStoreId } = useContext(MyContext);
	const [range, setRange] = useState({});
	const [status, setStatus] = useState('');
	const [fulfillmentType, setFulfillmentType] = useState('');
	const [page, setPage] = useState(0);
	const [report, setReport] = useState(null);
	const [loading, setLoading] = useState(true);
	const [artifactRow, setArtifactRow] = useState(null);

	useEffect(() => {
		setLoading(true);
		getCustomFulfillmentReport({
			storeId: activeStoreId || undefined,
			dateFrom: range.from || undefined,
			dateTo: range.to || undefined,
			status: status || undefined,
			fulfillmentType: fulfillmentType || undefined,
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
		}).then((result) => {
			if (result?.success) setReport(result.data);
			setLoading(false);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStoreId, range.from, range.to, status, fulfillmentType, page]);

	const filterFrom = (setter) => (e) => {
		setter(e.target.value);
		setPage(0);
	};

	const typeOptions = report?.filters?.fulfillment_types || ['PARTNER_LAB'];

	const columns = [
		{ 
			key: 'path', 
			header: 'Trigger', 
			render: value => value ? value.toUpperCase() : '—' 
		},
		{ key: 'fulfillment_type', header: 'Type', render: (value) => value || '—' },
		// { key: 'event_type', header: 'Event' },
		{
			key: 'status',
			header: 'Status',
			render: (value) => (
				<StatusBadge status={value === 'success' ? 'fulfilled' : 'failed'} text={value} />
			),
		},
		{
			key: 'order_number',
			header: 'Order',
			render: (value, row) =>
				value ? (
					<Link to={`/orders/${row.order_id}`} className='hover:underline'>
						#{value}
					</Link>
				) : (
					''
				),
		},
		{
			key: 'message',
			header: 'Detail',
			sortable: false,
			render: (value) =>
				value ? (
					<Tooltip content={value} icon={MessageSquare} />
				) : (
					<span className='text-gray-300'>—</span>
				),
		},
		{
			key: 'received_at',
			header: 'When',
			render: (value) => (value ? new Date(value).toLocaleString() : ''),
		},
		{
			key: 'artifact',
			header: 'Sent File',
			sortable: false,
			render: (_value, row) =>
				row.artifact_kind ? (
					<button
						type='button'
						className='text-blue-600 hover:underline'
						onClick={() => setArtifactRow(row)}
					>
						{row.artifact_kind === 'excel' ? 'View Excel' : 'View email'}
					</button>
				) : (
					<span className='text-gray-300'>—</span>
				),
		},
	];

	const pendingColumns = [
		{ key: 'partner_name', header: 'Partner', render: (value) => value || '—' },
		{ key: 'partner_email', header: 'Email' },
		{ key: 'fulfillment_type', header: 'Fulfillment Type' },
		{ key: 'line_item_count', header: 'Line Items' },
		{
			key: 'oldest_order_accepted_at',
			header: 'Oldest Order Accepted',
			render: (value) => (value ? new Date(value).toLocaleString() : ''),
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Custom Fulfillment History</h1>
				<ExportButton
					fileName='custom-fulfillment-report'
					sections={[
						{
							data: report?.rows || [],
							columns: columns.map((c) => c.header),
							columnKeys: columns.map((c) => c.key),
							columnTypes: ['text', 'text', 'text', 'text', 'text', 'text', 'date'],
						},
					]}
				/>
			</div>

			<div className='flex gap-4 mb-6'>
				<div>
					<label>Date Range</label>
					<DateRangePicker
						value={range}
						onChange={(item) => {
							setRange(item);
							setPage(0);
						}}
						className='w-72'
					/>
				</div>
				<div>
					<label>Status</label>
					<select className='form-select' value={status} onChange={filterFrom(setStatus)}>
						<option value=''>All</option>
						<option value='success'>Successful</option>
						<option value='failed'>Failed</option>
					</select>
				</div>
				<div>
					<label>Fulfillment Type</label>
					<select
						className='form-select'
						value={fulfillmentType}
						onChange={filterFrom(setFulfillmentType)}
					>
						<option value=''>All</option>
						{typeOptions.map((t) => (
							<option key={t} value={t}>
								{t}
							</option>
						))}
					</select>
				</div>
			</div>

			{loading || !report ? (
				<div className='text-sm text-gray-400'>Loading...</div>
			) : (
				<>
					<div className='grid grid-cols-5 gap-4 mb-8'>
						<StatCard label='Total Events' value={report.summary.total} />
						<StatCard label='Succeeded' value={report.summary.succeeded} />
						<StatCard label='Failed' value={report.summary.failed} />
						<StatCard
							label='Success Rate'
							value={report.summary.success_rate != null ? `${report.summary.success_rate}%` : '-'}
						/>
						<StatCard label='Pending Export' value={report.pending?.count ?? 0} />
					</div>

					<DataTable
						columns={columns}
						data={report.rows}
						loading={loading}
						emptyHeading='No custom fulfillment activity in range'
					/>
					<Pagination
						pageCount={Math.ceil((report.pagination?.total || 0) / PAGE_SIZE)}
						currentPage={page}
						onPageChange={setPage}
					/>

					{report.pending?.count ? (
						<div className='mt-10'>
							<h2 className='text-base font-medium mb-3'>Awaiting Next Weekly Export</h2>
							<DataTable
								columns={pendingColumns}
								data={report.pending.by_partner}
								emptyHeading='Nothing pending'
							/>
						</div>
					) : null}
				</>
			)}

			<CustomFulfillmentArtifactModal
				open={!!artifactRow}
				setOpen={(next) => !next && setArtifactRow(null)}
				row={artifactRow}
				storeId={activeStoreId}
			/>
		</div>
	);
}
